import { useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import { authentication } from "@microsoft/teams-js";
import { getTeamsSsoToken } from "../hooks/teamsSso";
import { TeamsUserData } from "../hooks/useTeamsUser";
import { useTranslation } from "../i18n/LocaleContext";
import {
  buildApiUrl,
  buildPopupUrl,
  clearBrowserQuery,
  OnboardingQueryState,
  readResponseError,
} from "./consentFlow";
import "./OnboardingScreen.css";

type PermissionsRenewalScreenProps = {
  teamsUser?: TeamsUserData | null;
};

type RenewalStage = "intro" | "working" | "success" | "failure";

type FailureDetail = {
  code: string;
  description: string;
  uri: string;
};

const EMPTY_FAILURE: FailureDetail = { code: "", description: "", uri: "" };

export default function PermissionsRenewalScreen({ teamsUser }: PermissionsRenewalScreenProps) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<RenewalStage>("intro");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusIsError, setStatusIsError] = useState(false);
  const [failureDetail, setFailureDetail] = useState<FailureDetail>(EMPTY_FAILURE);

  function fail(detail: Partial<FailureDetail>) {
    setFailureDetail({ ...EMPTY_FAILURE, ...detail });
    setStage("failure");
  }

  async function confirmRenewal(queryState: OnboardingQueryState) {
    const accessToken = await getTeamsSsoToken();
    const response = await fetch(buildApiUrl("/api/bearer/renewTenantPermissions"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        tenantId: queryState.tenant || teamsUser?.tenantId || "",
        admin_consent: queryState.adminConsent,
        state: queryState.state,
        scope: queryState.scope,
      }),
    });

    if (!response.ok) {
      fail({ description: await readResponseError(response) });
      return;
    }

    setStage("success");
  }

  async function handleConsent() {
    setStage("working");
    setStatusMessage(t("permissionsRenewal.status.opening"));
    setStatusIsError(false);

    try {
      // Mesmo popup e mesmo fluxo de navegação do onboarding: o popup obtém a sessão em
      // /getSession, redireciona para a página de consentimento da Microsoft e devolve os
      // parâmetros do callback aqui. Os parâmetros ficam apenas em memória (sem escrever na
      // URL), para que um reload não seja confundido com o fluxo de onboarding.
      const popupResult = await authentication.authenticate({
        url: buildPopupUrl(),
        width: 600,
        height: 720,
      });

      const queryState = JSON.parse(popupResult) as OnboardingQueryState;

      if (queryState.hasError || queryState.adminConsent !== "true") {
        fail({
          code: queryState.error,
          description: queryState.errorDescription,
          uri: queryState.errorUri,
        });
        return;
      }

      await confirmRenewal(queryState);
    } catch (error) {
      fail({ description: error instanceof Error ? error.message : String(error) });
    }
  }

  function handleOpenApplication() {
    clearBrowserQuery();
    window.location.reload();
  }

  function handleRetry() {
    setFailureDetail(EMPTY_FAILURE);
    setStatusMessage("");
    setStatusIsError(false);
    setStage("intro");
  }

  if (stage === "success") {
    return (
      <div className="softphone-shell onboarding-shell">
        <article className="status-card onboarding-card">
          <div className="onboarding-card-header">
            <div>
              <p className="eyebrow">{t("permissionsRenewal.eyebrow")}</p>
              <h1 className="onboarding-title">{t("permissionsRenewal.success.title")}</h1>
              <p className="onboarding-card-copy">{t("permissionsRenewal.success.copy")}</p>
            </div>
          </div>

          <div className="onboarding-actions">
            <Button variant="contained" onClick={handleOpenApplication}>
              {t("permissionsRenewal.success.openButton")}
            </Button>
          </div>
        </article>
      </div>
    );
  }

  if (stage === "failure") {
    return (
      <div className="softphone-shell onboarding-shell">
        <article className="status-card onboarding-card">
          <div className="onboarding-card-header">
            <div>
              <p className="eyebrow">{t("permissionsRenewal.eyebrow")}</p>
              <h1 className="onboarding-title">{t("permissionsRenewal.failure.title")}</h1>
              <p className="onboarding-card-copy">{t("permissionsRenewal.failure.copy")}</p>
            </div>
          </div>

          <div className="onboarding-form">
            <Alert severity="error">
              <strong>{t("permissionsRenewal.failure.codeLabel")}</strong>{" "}
              {failureDetail.code || "admin_consent=false"}
            </Alert>
            {failureDetail.description ? (
              <Alert severity="warning">
                <pre className="onboarding-error-pre">{failureDetail.description}</pre>
              </Alert>
            ) : null}
            {failureDetail.uri ? (
              <Alert severity="info">
                <a href={failureDetail.uri} target="_blank" rel="noreferrer">
                  {failureDetail.uri}
                </a>
              </Alert>
            ) : null}
          </div>

          <div className="onboarding-actions">
            <Button variant="contained" onClick={handleRetry}>
              {t("permissionsRenewal.failure.retryButton")}
            </Button>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="softphone-shell onboarding-shell">
      <article className="status-card onboarding-card">
        <div className="onboarding-card-header">
          <div>
            <p className="eyebrow">{t("permissionsRenewal.eyebrow")}</p>
            <h1>{t("permissionsRenewal.title")}</h1>
            <p className="onboarding-card-copy">{t("permissionsRenewal.copy")}</p>
          </div>
        </div>

        {statusMessage ? (
          <Alert severity={statusIsError ? "error" : "info"} className="onboarding-status">
            {statusMessage}
          </Alert>
        ) : null}

        <div className="onboarding-actions">
          <Button variant="contained" onClick={handleConsent} disabled={stage === "working"}>
            {stage === "working" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("permissionsRenewal.consentButton")
            )}
          </Button>
        </div>
      </article>
    </div>
  );
}
